import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, CircularProgress } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { parseISO } from 'date-fns';
import { Users } from 'lucide-react';

import { LISTING_BUYER_LEADS_OVER_TIME } from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

import {
  CHART_LEGEND_SLOT_PROPS,
  CHART_TOOLTIP_SX,
  ChartEmptyState,
} from './ChartControls';
import {
  type Granularity,
  SERIES_COLORS,
  aggregateStacked,
  buildBuckets,
} from './chartUtils';

// Embeddable buyer-leads-by-source chart, rendered inside the funnel's Buyer
// leads drill-down.
export const ListingBuyerLeadsBody = ({
  lotId,
  start,
  end,
  granularity,
}: {
  lotId: string;
  start: Date;
  end: Date;
  granularity: Granularity;
}) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(LISTING_BUYER_LEADS_OVER_TIME, {
    variables: { lotId },
  });

  // Sources are free-form strings; give the common ones friendly labels and
  // fall back to the raw value (capitalized) for anything else.
  const sourceLabel = (source: string | null | undefined): string => {
    switch (source) {
      case 'matching':
        return t('Matching');
      case 'manual':
        return t('Manual');
      case 'mls':
        return t('MLS');
      case null:
      case undefined:
      case '':
        return t('Other');
      default:
        return source.charAt(0).toUpperCase() + source.slice(1);
    }
  };

  const chart = useMemo(() => {
    const buyerLeads = data?.buyer_leads ?? [];
    const buckets = buildBuckets(start, end, granularity);
    const series = aggregateStacked(
      buyerLeads,
      lead => parseISO(lead.created_at),
      lead => sourceLabel(lead.source),
      buckets,
    );
    return { labels: buckets.labels, series };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, start, end, granularity, t]);

  const hasLeads = chart.series.some(series =>
    series.data.some(value => value > 0),
  );

  if (loading) {
    return (
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error != null) {
    return (
      <ChartEmptyState
        icon={<Users size={22} />}
        message={t('Failed to load data')}
      />
    );
  }

  if (!hasLeads) {
    return (
      <ChartEmptyState
        icon={<Users size={22} />}
        message={t('No buyer leads in this period')}
      />
    );
  }

  return (
    <BarChart
      height={260}
      xAxis={[
        {
          data: chart.labels,
          scaleType: 'band',
          tickLabelStyle: { fontSize: 10 },
        },
      ]}
      series={chart.series.map((item, index) => ({
        data: item.data,
        label: item.label,
        stack: 'total',
        color: SERIES_COLORS[index % SERIES_COLORS.length],
      }))}
      margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
      slotProps={{
        tooltip: { sx: CHART_TOOLTIP_SX },
        legend: CHART_LEGEND_SLOT_PROPS,
      }}
    />
  );
};
